import React, {useEffect, useMemo, useState} from 'react';
import {Button, Modal, message} from 'antd';
import advertiserService from '../../../services/advertiserService';
import TableComponent from '../../../components/molecules/Table/component';
import {bestAdvertisersTableColumns} from '../../../components/molecules/Table/columns';
import {RootState} from '../../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import {handleLogout, createUserTableChangeHandler, useIsMobile} from '../../../utils/helpers';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {mobileRenderers} from "../../../components/molecules/Table/tableMobileRenderers";

const {confirm, success} = Modal;

const BestAdvertisersPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const isMobile = useIsMobile();

    const [advertiserList, setAdvertiserList] = useState<any[]>([]);
    const showBecomeAdvertiser = ['promoter', 'partner'].includes(role) && currentUser.roles.length === 1;

    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        userType: '',
        search: '',
        sortBy: '',
        sortDirection: 'desc' as 'asc' | 'desc'
    });

    const onTableChange = useMemo(() =>
        createUserTableChangeHandler(filters, setFilters), [filters]
    );

    const onLogout = () => {
        handleLogout(dispatch, navigate)();
    };

    useEffect(() => {
        const fetchAdvertisers = async () => {
            setLoading(true);

            try {
                const data = await advertiserService.getBestAdvertisers(filters);
                setAdvertiserList(data);
            } catch (error) {
                console.error('Error fetching advertisers:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAdvertisers();
    }, [filters]);

    const handleRowClick = (record: any) => {
        navigate(`/advertisers/${record._id}`);
    };

    const handleBecomeAdvertiser = () => {
        confirm({
            title: 'Add Advertiser Role',
            icon: <ExclamationCircleOutlined/>,
            content: (
                <>
                    By confirming, you’ll add the <b>Advertiser</b> role to your account.
                    You can explore the new Advertiser Portal right away, and—when you’re ready
                    to actually launch campaigns—you’ll find instructions there on activating
                    your advertiser subscription.
                </>
            ),
            okText: 'Confirm',
            cancelText: 'Cancel',
            async onOk() {
                try {
                    const res = await advertiserService.becomeAdvertiser();
                    if (res.status === 200) {
                        success({
                            title: 'Success!',
                            content: (
                                <>
                                    Your account now has both {role} and advertiser roles.
                                    To apply this change, please log out and then log back in.
                                </>
                            ),
                            okText: 'Log out',
                            onOk: onLogout
                        });
                    } else {
                        message.error('Error while adding advertiser role.');
                    }
                } catch (err: any) {
                    message.error(err.message || 'Error while adding advertiser role.');
                }
            }
        });
    };

    return (
        <div style={{position: 'relative', paddingTop: isMobile ? (showBecomeAdvertiser ? '50px' : '40px') : '20px'}}>
            {showBecomeAdvertiser && (
                <div style={{position: 'absolute', top: 0, right: 0, margin: '10px'}}>
                    <Button
                        type="primary"
                        size={isMobile ? 'small' : 'middle'}
                        style={isMobile ? {fontSize: 12, padding: '0 10px', height: 28} : undefined}
                        onClick={handleBecomeAdvertiser}
                    >
                        Become an advertiser
                    </Button>
                </div>
            )}
            <TableComponent columns={bestAdvertisersTableColumns} title={"Best advertisers"} data={advertiserList || []}
                            onRowClick={handleRowClick} loading={loading} onChange={onTableChange} isMobile={isMobile}
                            mobileVariant="bestAdvertisers" mobileRenderers={mobileRenderers}
                            rowKey={(row) => row._id}
            />
        </div>
    );
};

export default BestAdvertisersPage;
